type Matrix = [number, number, number][];

/**
 * Transforms an RGB color based on the specified color vision deficiency type.
 * @param color - The original RGB color as a string in the format "R,G,B".
 * @param cvdType - The type of color vision deficiency ("deuteranopia", "protanopia", "tritanopia").
 * @returns The transformed RGB color as a string in the format "R,G,B".
 */
const convertCVD = (color: string | null, cvdType: string | null): string  => {
  if (!color) return "";
  if (!cvdType) return color;
  // Matrix transformations for each colorblindness type
  const transformations: Record<string, Matrix> = {
    deuteranopia: [
      [0.625, 0.375, 0.0],
      [0.7, 0.3, 0.0],
      [0.0, 0.3, 0.7],
    ],
    protanopia: [
      [0.567, 0.433, 0.0],
      [0.558, 0.442, 0.0],
      [0.0, 0.242, 0.758],
    ],
    tritanopia: [
      [0.95, 0.05, 0.0],
      [0.0, 0.433, 0.567],
      [0.0, 0.475, 0.525],
    ],
  };

  // Parse the RGB color
  const rgb = color.split(",").map((value) => parseInt(value.trim(), 10));

  // Validate input
  if (rgb.length !== 3 || rgb.some((value) => isNaN(value))) {
    throw new Error(`Invalid RGB color format: "${color}". Expected "R,G,B".`);
  }

  const [r, g, b] = rgb;

  // Get the transformation matrix for the specified cvdType
  const matrix = transformations[cvdType];

  if (!matrix) {
    // Return original color if cvdType is not valid
    return color;
  }

  // Apply the matrix transformation
  const newR = Math.round(
    matrix[0][0] * r + matrix[0][1] * g + matrix[0][2] * b
  );
  const newG = Math.round(
    matrix[1][0] * r + matrix[1][1] * g + matrix[1][2] * b
  );
  const newB = Math.round(
    matrix[2][0] * r + matrix[2][1] * g + matrix[2][2] * b
  );

  // Ensure values are within RGB bounds
  const clamp = (value: number) => Math.min(255, Math.max(0, value));

  return `${clamp(newR)},${clamp(newG)},${clamp(newB)}`;
}

export default convertCVD;